import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { AUDIT_ACTION_VALUES, auditActionLabel, auditActionsByGroup } from "@yard/protocol";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ListFilter,
  ScrollText,
  SearchIcon,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { LogoMark } from "@/components/logo";
import { PageHeader, PageToolbar } from "@/components/page-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogInset,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { GradientAvatar } from "@/components/ui/gradient-avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import type { AuditEntry } from "@/lib/types";
import { cn, relativeTime } from "@/lib/utils";

const PAGE_SIZE = 100;

/** Sentinel for "no filter": `<Select>` cannot carry an empty-string value. */
const ANY = "any";

/**
 * Filters live in the URL, not in component state, so a filtered view is
 * something you can send to someone. That is most of the point of an audit log
 * with filters at all — "look at what happened to this device" is a link.
 */
const search = z.object({
  action: z.enum(AUDIT_ACTION_VALUES).optional(),
  actorUserId: z.string().optional(),
  targetId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(0).catch(0).default(0),
});

type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/admin/audit")({
  beforeLoad: ({ context }) => {
    // A UX guard, not a security boundary — `adminProcedure` is the real one.
    if (context.user?.role !== "admin") throw redirect({ to: "/devices" });
  },
  validateSearch: search,
  component: AuditPage,
});

/** One day's entries in one card, as Vercel's Activity feed is cut by date. */
const FEED_CARD =
  "overflow-hidden rounded-lg bg-background-100 shadow-border divide-y divide-gray-alpha-400";

function AuditPage() {
  const params = Route.useSearch();
  const navigate = Route.useNavigate();
  /** The row whose whole record is on screen; the feed itself only clamps. */
  const [openEntry, setOpenEntry] = useState<AuditEntry | null>(null);

  /** Any filter change resets to the first page: page 4 of a new query is nothing. */
  const setFilter = (patch: Partial<Search>) =>
    navigate({ search: (current) => ({ ...current, ...patch, page: 0 }) });

  const { data, isFetching } = useQuery(
    trpc.admin.audit.queryOptions({
      limit: PAGE_SIZE,
      offset: params.page * PAGE_SIZE,
      action: params.action ? [params.action] : undefined,
      actorUserId: params.actorUserId,
      targetId: params.targetId,
      from: params.from ? new Date(params.from) : undefined,
      // A date input means the whole day, not midnight at its start.
      to: params.to ? endOfDay(params.to) : undefined,
    }),
  );

  const { data: users } = useQuery(trpc.admin.users.queryOptions({ limit: 200, offset: 0 }));

  const entries = data?.items ?? [];
  const total = data?.total ?? 0;
  const first = total === 0 ? 0 : params.page * PAGE_SIZE + 1;
  const last = params.page * PAGE_SIZE + entries.length;
  const filtered = Boolean(
    params.action || params.actorUserId || params.targetId || params.from || params.to,
  );

  const clearFilters = () =>
    navigate({
      search: {
        page: 0,
        action: undefined,
        actorUserId: undefined,
        targetId: undefined,
        from: undefined,
        to: undefined,
      },
    });

  const open = (entry: AuditEntry) => setOpenEntry(entry);

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Audit log"
        meta={
          data && (
            <span className="tabular-nums">
              {total} {total === 1 ? "entry" : "entries"}
            </span>
          )
        }
      />

      {/* Vercel's Activity filters. On a phone: search on its own row, the two
          selects sharing one, the date range on a third. */}
      <PageToolbar>
        <TargetFilter value={params.targetId} onChange={(targetId) => setFilter({ targetId })} />

        <Label htmlFor="filter-who" className="sr-only">
          Who
        </Label>
        <Select
          value={params.actorUserId ?? ANY}
          onValueChange={(value) => setFilter({ actorUserId: value === ANY ? undefined : value })}
        >
          <SelectTrigger
            id="filter-who"
            prefix={<UsersRound />}
            className="h-10 w-[calc(50%-4px)] sm:w-44"
          >
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All users</SelectItem>
            {users?.users.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.name ?? u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label htmlFor="filter-action" className="sr-only">
          Action
        </Label>
        <Select
          value={params.action ?? ANY}
          onValueChange={(value) =>
            setFilter({
              action: value === ANY ? undefined : (value as NonNullable<Search["action"]>),
            })
          }
        >
          {/* Wide enough for the longest label, "Released at the session
              limit", on one line. */}
          <SelectTrigger
            id="filter-action"
            prefix={<ListFilter />}
            className="h-10 w-[calc(50%-4px)] sm:w-64"
          >
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All actions</SelectItem>
            {auditActionsByGroup().map(({ group, actions }) => (
              <SelectGroup key={group}>
                <SelectLabel>{group}</SelectLabel>
                {actions.map((action) => (
                  <SelectItem key={action.value} value={action.value}>
                    {action.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>

        {/* Vercel's date-range button, built from the two native inputs the
            filter has always used: one control, one calendar glyph. */}
        <div className="flex h-10 w-full min-w-0 items-stretch rounded-md bg-background-100 shadow-control transition-shadow duration-150 hover:shadow-control-hover has-[input:focus-visible]:shadow-focus-border! sm:w-auto">
          {/* Not on a phone: there the two dates need every pixel of the row. */}
          <span aria-hidden className="hidden items-center pl-3 text-gray-900 sm:flex">
            <CalendarDays className="size-4" />
          </span>
          <DateFilter
            id="filter-from"
            label="From"
            value={params.from}
            onChange={(from) => setFilter({ from })}
          />
          <span aria-hidden className="my-2.5 w-px shrink-0 bg-gray-alpha-400" />
          <DateFilter
            id="filter-to"
            label="To"
            value={params.to}
            onChange={(to) => setFilter({ to })}
          />
        </div>

        {filtered && (
          <Button variant="tertiary" className="h-10" prefix={<X />} onClick={clearFilters}>
            Clear
          </Button>
        )}
      </PageToolbar>

      {entries.length === 0 ? (
        isFetching ? (
          <ul aria-label="Loading" className={FEED_CARD}>
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="flex h-12 items-center gap-3 px-4">
                <Skeleton shape="circle" className="size-6" />
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3.5 w-40" />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<ScrollText />}
            title={filtered ? "Nothing matches these filters" : "Nothing recorded yet"}
            description={
              filtered
                ? "Try a wider date range, or fewer filters."
                : "Every action lands here, from reservations to installs to setting changes."
            }
          >
            {filtered && (
              <Button variant="secondary" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </EmptyState>
        )
      ) : (
        <div className="flex flex-col gap-8">
          {groupByDay(entries).map((day) => (
            <section key={day.key} aria-label={day.label}>
              <h2 className="mb-3 text-gray-1000 text-heading-16">{day.label}</h2>

              {/* One line per entry from `lg`, Vercel's sentence: who, what,
                  to what, then the detail fading into grey. Below that there
                  is no width for it, so each entry stacks — with the same
                  tap target: the whole entry. */}
              <ul className={cn(FEED_CARD, "hidden lg:block")}>
                {day.entries.map((entry) => (
                  <li
                    key={entry.id}
                    className="relative flex h-12 items-center gap-3 px-4 transition-colors hover:bg-gray-alpha-100 has-[>button:focus-visible]:bg-gray-alpha-100"
                  >
                    {/* The whole row opens the entry: a button stretched under
                        the row's text. It sits beside the target link rather
                        than around it — a link inside a button is invalid — and
                        the link is lifted above it so it stays clickable. */}
                    <button
                      type="button"
                      aria-label={`Show the whole entry: ${auditActionLabel(entry.action)}`}
                      className="absolute inset-0 cursor-pointer outline-none"
                      onClick={() => open(entry)}
                    />
                    <ActorAvatar entry={entry} />
                    <div className="flex min-w-0 flex-1 items-baseline gap-1.5 text-label-14">
                      <span className="max-w-48 shrink-0 truncate font-medium text-gray-1000">
                        {actorLabel(entry)}
                      </span>
                      <span className="shrink-0 text-gray-900" title={entry.action}>
                        {auditActionLabel(entry.action)}
                      </span>
                      {entry.targetId && (
                        <span className="relative z-10 min-w-0 max-w-72 shrink truncate font-medium text-gray-1000 text-label-13-mono">
                          <Target entry={entry} />
                        </span>
                      )}
                      {entry.metadata != null && (
                        <span className="ml-3 min-w-0 flex-1 truncate text-gray-700">
                          <Detail entry={entry} />
                        </span>
                      )}
                    </div>
                    <span
                      className="shrink-0 text-gray-900 text-label-14 tabular-nums"
                      title={new Date(entry.at).toLocaleString()}
                    >
                      {shortAgo(entry.at)}
                    </span>
                  </li>
                ))}
              </ul>

              <ul className={cn(FEED_CARD, "lg:hidden")}>
                {day.entries.map((entry) => (
                  <li key={entry.id}>
                    {/* No target link in here: a link inside a button is invalid,
                        and the entry it opens carries the link. */}
                    <button
                      type="button"
                      className="flex w-full items-start gap-3 px-4 py-3 text-left outline-none transition-colors hover:bg-gray-alpha-100 focus-visible:bg-gray-alpha-100"
                      onClick={() => open(entry)}
                    >
                      <ActorAvatar entry={entry} className="-mt-0.5" />
                      <div className="grid min-w-0 flex-1 gap-0.5">
                        <div className="flex min-w-0 items-baseline justify-between gap-3">
                          <span className="min-w-0 truncate text-label-14">
                            <span className="font-medium text-gray-1000">{actorLabel(entry)}</span>{" "}
                            <span className="text-gray-900">{auditActionLabel(entry.action)}</span>
                          </span>
                          <span
                            className="shrink-0 text-gray-900 text-label-13 tabular-nums"
                            title={new Date(entry.at).toLocaleString()}
                          >
                            {shortAgo(entry.at)}
                          </span>
                        </div>
                        {entry.targetId && (
                          <p className="truncate font-medium text-gray-1000 text-label-13-mono">
                            {entry.targetId}
                          </p>
                        )}
                        {entry.metadata != null && (
                          <div className="truncate text-gray-700 text-label-13">
                            <Detail entry={entry} />
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <AuditEntryDialog entry={openEntry} onClose={() => setOpenEntry(null)} />

      {total > 0 && (
        <div className="mt-6 flex items-center justify-between gap-3">
          <span className="text-gray-900 text-label-14 tabular-nums">
            Showing {first}–{last} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="small"
              className="pointer-coarse:h-10"
              prefix={<ChevronLeft />}
              disabled={params.page === 0}
              onClick={() => navigate({ search: (c) => ({ ...c, page: c.page - 1 }) })}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="small"
              className="pointer-coarse:h-10"
              suffix={<ChevronRight />}
              disabled={last >= total}
              onClick={() => navigate({ search: (c) => ({ ...c, page: c.page + 1 }) })}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Debounced, because this one is typed rather than picked: a request per
 * keystroke would also mean a URL history entry per keystroke.
 */
function TargetFilter({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");

  // Follow the URL when it changes from outside — Clear, or a pasted link.
  useEffect(() => setDraft(value ?? ""), [value]);

  useEffect(() => {
    const current = value ?? "";
    if (draft === current) return;
    const timer = setTimeout(() => onChange(draft.trim() || undefined), 300);
    return () => clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <div className="w-full sm:w-auto sm:min-w-56 sm:flex-1">
      <Label htmlFor="filter-target" className="sr-only">
        Target
      </Label>
      <Input
        id="filter-target"
        className="h-10 [&>input]:h-full [&>input]:font-mono [&>input]:placeholder:font-sans"
        prefix={<SearchIcon className="size-4" />}
        prefixStyling={false}
        placeholder="Device or provider id"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
    </div>
  );
}

/**
 * One half of the date range: a native date input with its label inside the
 * control. The browser's own picker glyph is hidden in favour of the range's
 * single calendar icon, so a click anywhere on the field opens the picker.
 */
function DateFilter({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 pr-2 pl-3 sm:flex-none sm:gap-2 sm:px-3">
      <Label htmlFor={id} className="text-gray-900 text-label-14">
        {label}
      </Label>
      <input
        id={id}
        type="date"
        className={cn(
          "h-full min-w-0 flex-1 cursor-pointer bg-transparent text-label-14 tabular-nums outline-none pointer-coarse:text-base sm:w-[6.5rem] sm:flex-none",
          "[&::-webkit-calendar-picker-indicator]:hidden",
          value ? "text-gray-1000" : "text-gray-700",
        )}
        value={value ?? ""}
        onClick={(e) => {
          // Throws where the page may not open one (an iframe); typing still works.
          try {
            e.currentTarget.showPicker();
          } catch {}
        }}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    </div>
  );
}

/**
 * Vercel's Activity time: "3m", "2h", "4d" — short enough to sit at the end of
 * a phone's row beside the sentence. The exact time is the element's title,
 * and the entry's dialog.
 */
function shortAgo(at: AuditEntry["at"]) {
  const seconds = Math.max(0, (Date.now() - new Date(at).getTime()) / 1000);
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}

/** The last moment of the given day, so "to: today" includes today. */
function endOfDay(date: string): Date {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Entries cut into local days, newest first, each headed the way Vercel heads
 * its Activity months. The query is already newest-first, so a day's entries
 * are contiguous and one pass is enough.
 */
function groupByDay(entries: AuditEntry[]) {
  const days: { key: string; label: string; entries: AuditEntry[] }[] = [];
  for (const entry of entries) {
    const at = new Date(entry.at);
    const key = at.toDateString();
    const day = days.at(-1);
    if (day?.key === key) day.entries.push(entry);
    else days.push({ key, label: dayLabel(at), entries: [entry] });
  }
  return days;
}

function dayLabel(at: Date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (at.toDateString() === today.toDateString()) return "Today";
  if (at.toDateString() === yesterday.toDateString()) return "Yesterday";
  return at.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: at.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
}

/**
 * The face beside an entry: a person's gradient (seeded by email, so it is
 * the same disc as on the Users page), a blank one for a deleted user, and
 * the product's own mark for the system — as Vercel shows its ▲ for its own.
 */
function ActorAvatar({ entry, className }: { entry: AuditEntry; className?: string }) {
  if (entry.actorName) {
    return (
      <Avatar size="sm" className={className}>
        <AvatarFallback>
          <GradientAvatar seed={entry.actorEmail ?? entry.actorUserId ?? entry.actorName} />
        </AvatarFallback>
      </Avatar>
    );
  }
  if (entry.actorUserId) {
    return (
      <Avatar size="sm" className={className}>
        <AvatarFallback className="bg-gray-200 text-gray-700">
          <UserRound className="size-3.5" />
        </AvatarFallback>
      </Avatar>
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-1000 text-background-100",
        className,
      )}
    >
      <LogoMark className="size-3.5" />
    </span>
  );
}

/**
 * Who did it.
 *
 * Three cases, and an audit log must not conflate them: the reaper and provider
 * disconnects have no actor at all, a deleted user leaves an id with no row,
 * and everyone else has a name. Showing "System" for a deleted user would say
 * nobody did something a person did.
 */
function Actor({ entry }: { entry: AuditEntry }) {
  if (entry.actorName) {
    return (
      <div className="min-w-0">
        <div className="truncate text-gray-1000">{entry.actorName}</div>
        {entry.actorEmail && (
          <div className="truncate text-gray-900 text-label-13">{entry.actorEmail}</div>
        )}
      </div>
    );
  }
  if (entry.actorUserId) {
    return (
      <div className="min-w-0">
        <div className="text-gray-900">Deleted user</div>
        <div className="truncate text-gray-900 text-label-13-mono" title={entry.actorUserId}>
          {entry.actorUserId}
        </div>
      </div>
    );
  }
  return <span className="text-gray-900">System</span>;
}

/** `Actor`'s three cases as one line of text, for the feed rows. */
function actorLabel(entry: AuditEntry) {
  if (entry.actorName) return entry.actorName;
  return entry.actorUserId ? "Deleted user" : "System";
}

/** A device target is a link: the row is usually the start of a question. */
function Target({ entry }: { entry: AuditEntry }) {
  if (!entry.targetId) return <span className="text-gray-700">—</span>;
  if (entry.targetType !== "device") return <span>{entry.targetId}</span>;
  return (
    <Link
      to="/devices/$deviceId"
      params={{ deviceId: entry.targetId }}
      className="rounded-xs underline-offset-4 hover:underline focus-visible:shadow-focus-ring focus-visible:outline-none"
      // The row opens the entry; this link goes somewhere else entirely.
      onClick={(event) => event.stopPropagation()}
    >
      {entry.targetId}
    </Link>
  );
}

/**
 * One line, always.
 *
 * Every place it is shown truncates, so anything longer is cut rather than
 * allowed to push the row wider. The whole value is a click away in
 * `AuditEntryDialog` — nothing here is the only copy.
 */
function Detail({ entry }: { entry: AuditEntry }) {
  const metadata = entry.metadata as Record<string, unknown> | null;
  if (!metadata) return <span className="text-gray-700">—</span>;

  if (
    entry.action === "device.install" ||
    entry.action === "device.preload" ||
    entry.action === "device.preload.remove" ||
    entry.action === "device.file_pull"
  ) {
    return (
      <span>
        {String(metadata.filename ?? metadata.path ?? "unknown")}
        {typeof metadata.size === "number" && ` · ${formatBytes(metadata.size)}`}
        {metadata.ok === false && <span className="ml-1.5 text-red-900">failed</span>}
      </span>
    );
  }

  const reason = typeof metadata.reason === "string" ? metadata.reason : null;
  if (reason) return <span>{reason}</span>;

  return <span className="text-label-13-mono">{JSON.stringify(metadata)}</span>;
}

/**
 * The whole record, for when the row's one line was not enough.
 *
 * Every value here is shown in full and is selectable: an install's sha256
 * identifies the exact package, whether it was a transient session install or
 * a retained preload, so it must be copyable, not merely visible.
 */
function AuditEntryDialog({ entry, onClose }: { entry: AuditEntry | null; onClose: () => void }) {
  const metadata = entry?.metadata as Record<string, unknown> | null | undefined;

  return (
    <Dialog open={entry !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{entry ? auditActionLabel(entry.action) : ""}</DialogTitle>
          <DialogDescription className="text-copy-13-mono text-gray-900">
            {entry?.action}
          </DialogDescription>
        </DialogHeader>

        {entry && (
          <DialogInset className="bg-background-100 py-0">
            <dl className="grid min-w-0 divide-y divide-gray-alpha-400 text-label-14">
              <Row label="When">
                <span className="text-gray-1000 tabular-nums">
                  {new Date(entry.at).toLocaleString()}
                </span>{" "}
                <span className="text-gray-900">({relativeTime(entry.at)})</span>
              </Row>
              <Row label="Who">
                <div className="flex min-w-0 items-center gap-3">
                  <ActorAvatar entry={entry} />
                  <Actor entry={entry} />
                </div>
              </Row>
              <Row label="Target">
                <span className="text-gray-1000 text-label-13-mono">
                  {entry.targetType ? `${entry.targetType} · ` : ""}
                  <Target entry={entry} />
                </span>
              </Row>
              <Row label="Detail">
                {metadata ? (
                  <pre className="max-h-80 overflow-auto rounded-md bg-background-200 p-3 text-copy-13-mono text-gray-1000 shadow-border">
                    {JSON.stringify(formatAuditMetadata(metadata), null, 2)}
                  </pre>
                ) : (
                  <span className="text-gray-700">—</span>
                )}
              </Row>
            </dl>
          </DialogInset>
        )}

        <DialogFooter showCloseButton />
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid min-w-0 gap-1.5 py-3.5 sm:grid-cols-[6rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-gray-900 sm:pt-px">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** Keep byte counts readable in the audit dialog. */
function formatAuditMetadata(value: unknown, key?: string): unknown {
  if (key === "size" && typeof value === "number") {
    return formatBytes(value);
  }
  if (Array.isArray(value)) return value.map((item) => formatAuditMetadata(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entryValue]) => [
        entryKey,
        formatAuditMetadata(entryValue, entryKey),
      ]),
    );
  }
  return value;
}

function formatBytes(size: number) {
  if (size < 1024) return `${size} B`;
  const units = ["KB", "MB", "GB"];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
